// What came from the JOBS under this retainer, as opposed to from the retainer itself.
//
// THE TILES ABOVE DO NOT CHANGE, AND THAT IS THE WHOLE POINT. The retainer's revenue,
// expenses and profit are its own income unit's P&L — the same three numbers the
// dashboard, the profit history and the partner balance read for this retainer. A job
// under it that bills its own brokerage margin is a SEPARATE income unit: its revenue and
// its costs net against each other on that job, it carries its own profit split, and the
// settlement divides the two units independently. Folding its margin into the tile above
// would make this screen report a different "retainer profit" than every other screen in
// the system, and neither figure would be wrong — which is the worst kind of
// disagreement, because there would be no way to tell which one someone meant.
//
// So the job's money appears HERE, in its own section under its own heading. The combined line is a view of the engagement as a whole
// and is labelled as one; nothing is computed from it, and the server sends it as a
// display sum built from the same profitCalc map the job screens use, so it cannot drift
// from what those screens show.
//
// Each line carries BOTH sides — what the job billed and what it cost — because "where
// did this come from" is answered by the pair. A single net figure would hide that the
// photographer was paid at all.
//
// THE LINE'S COST IS THE COST ITS CHARGE IS MEASURED AGAINST, and only that. The costs we
// absorb on the same job net against this engagement and are already in its tiles above,
// so they appear on the box's other side among the engagement's own costs (EngagementCosts)
// — never under the job's name as well, and never in the row's arithmetic. Every line
// foots — charge − cost = margin — and so does the footer (npm run check:brokerage).
//
// SIDE BY SIDE: the jobs at the start (the right, in RTL), the engagement's costs at the
// end, the equation under both. The split follows the CARD's width, not the screen's, so a
// laptop with the sidebar open stacks before two tables of figures get cramped.
//
// EACH TABLE ENDS IN A TOTAL ROW, and those are the server's too: `brokerage.revenue`,
// `.cost` and `.profit` for the jobs, `brokerage.costs.total` (the expense tile) for the
// costs. A cost reads red and a profit green, as everywhere on the site.
//
// UNDER BOTH, THE RESULT: the two profits it is made of, each named, above the total net
// profit — large, green, centred across the card, so what came from the engagement and
// what came from the jobs reads before the figure they add up to. The first is the green
// tile above, repeated on purpose — a total whose addend sits a card away is a total
// nobody can read. All three are the server's (`retainer.profit.profit`,
// `brokerage.profit`, `brokerage.combined`); nothing is added up here.
//
// BESIDE IT, WHAT THE CLIENT WAS BILLED for the whole engagement — its own price, months
// and extras plus every charge on the jobs, whatever they cost. Revenue, so neither red nor
// green. The server's `brokerage.billed`, never a sum of the figures on this card.
import he from '../../i18n/he';
import Money, { Cost, Profit, isLoss } from '../../components/Money';
import DataTable from '../../components/DataTable';
import EngagementCosts from './EngagementCosts';

const Part = ({ label, value }) => (
  <div className="w-eq-part">
    <span className="label">{label}</span>
    <Profit value={value} className="value" />
  </div>
);

// `t` is the engagement's words (components/engagement): the same section under a retainer
// or a one-off project, with only the name of the engagement changed.
export default function BrokerageSection({ brokerage, ownProfit, t = he.retainers }) {
  if (!brokerage) return null;

  const columns = [
    { key: 'title', label: t.brokerageJob, primary: true, footer: he.common.total },
    {
      key: 'revenue', label: he.jobs.passThroughRevenue, className: 'num',
      render: (r) => <Money value={r.revenue} />, footer: <Money value={brokerage.revenue} />,
    },
    {
      key: 'cost', label: he.jobs.passThroughCost, className: 'num',
      render: (r) => <Cost value={r.cost} />, footer: <Cost value={brokerage.cost} />,
    },
    {
      key: 'profit', label: t.brokerageLineProfit, className: 'num',
      render: (r) => <Profit value={r.profit} />, footer: <Profit value={brokerage.profit} />,
    },
  ];

  return (
    <div className="card w-section w-split-card">
      <div className="w-split">
        <div className="w-split-side">
          <h2>{t.brokerageTitle}</h2>
          <DataTable columns={columns} rows={brokerage.lines} />
        </div>
        <EngagementCosts costs={brokerage.costs} t={t} />
      </div>

      <div className="w-bottom">
        {/* A loss turns the whole band red, not only its figure. */}
        <div className={`w-result${isLoss(brokerage.combined) ? ' loss' : ''}`}>
          <div className="w-eq-parts">
            <Part label={t.ownProfit} value={ownProfit} />
            <span className="w-eq-sign" aria-hidden="true">+</span>
            <Part label={t.brokerageProfit} value={brokerage.profit} />
          </div>
          <div className="w-eq-total">
            <span className="label">{t.combinedProfit}</span>
            <Profit value={brokerage.combined} className="value" />
          </div>
        </div>
        <div className="w-billed">
          <span className="label">{t.billedTotal}</span>
          <Money value={brokerage.billed} className="value" />
        </div>
      </div>
    </div>
  );
}
